import { ILogoStyle, ILogoWidgetStyle, ILinkData, ILogoWidgetData, LogoWidgetData, IElementWithTarget, LinkData } from "@pageBuilder/typings";
import { IRenderingCommonContext } from "@commonRendering/types";
import { CssProperties } from "../../../models/css-property";
import { LinkRenderer } from "../link.renderer";
import { WidgetRendererBase } from "../widget-renderer-base";
import { LogoScriptRenderer } from "./logo-script.renderer";

export class LogoWidgetRenderer extends WidgetRendererBase<ILogoWidgetData> {

    private readonly linkRenderer: LinkRenderer;
    private readonly scriptRenderer: LogoScriptRenderer;

    constructor(renderingCommonContext: IRenderingCommonContext) {
        super(renderingCommonContext);

        this.linkRenderer = new LinkRenderer();
        this.scriptRenderer = new LogoScriptRenderer();
    }

    protected createNewInternal(): HTMLElement {
        const element = this.createWidgetElement("logoWidget");

        return element;
    }

    protected renderInternal(element: HTMLElement, data: ILogoWidgetData): HTMLElement {
        if (this.linkNeedsToBeRerendered(element, data.linkData)) {
            this.rerenderLink(element, data.linkData);
        }

        if (this.logoOrTitleNeedsToBeRerendered(element, data)) {
            this.updateElementAttributes(element, data);
            this.renderScripts(element);
        }

        this.renderStyles(element, data);

        return element;
    }

    private updateElementAttributes(element: HTMLElement, data: ILogoWidgetData): void {
        element.setAttribute("data-logo-id", data.preferredLogoId || "");
        element.setAttribute("data-alt-text", data.altText);
    }

    private renderScripts(element: HTMLElement): void {
        this.scriptRenderer.renderCommon(this.renderedDocument);
        this.scriptRenderer.renderLocal(element);
    }

    private renderLink(element: HTMLElement, data: ILinkData | undefined): void {
        if (!data || data.type === "none") {
            return;
        }
        const content = element.querySelector(".widget-content") as HTMLElement;
        let link = element.querySelector<HTMLAnchorElement>("a");
        if (!link) {
            link = document.createElement("a");
            content.appendChild(link);
        }
        link.setAttribute("tabindex", "-1");

        this.linkRenderer.render(link, data);
    }

    private removeLink(element: HTMLElement): void {
        const link = element.querySelector<HTMLAnchorElement>("a");
        link?.remove();
    }

    protected renderStyles(element: HTMLElement, data: ILogoWidgetData): void {
        this.renderBaseStyles(element, data.style);

        if (!data.style?.logoStyle) {
            this.cleanupStyle(element);
            return;
        }

        this.renderBorderStyles(element, data.style.logoStyle);
        this.renderContentStyles(element, data.style.logoStyle);
    }

    private renderBaseStyles(element: HTMLElement, style: ILogoWidgetStyle | null): void {
        this.renderHeightStyles(element, style);
        this.animationRenderer.setAnimationStyles(element, style?.animation);
    }

    private renderHeightStyles(element: HTMLElement, style: ILogoWidgetStyle | null): void {
        style?.heightData?.height
            ? element.style.height = `${style.heightData.height}px`
            : element.style.height = `${LogoWidgetData.defaultStyle.heightData?.height}px`;
    }

    private renderBorderStyles(element: HTMLElement, style: ILogoStyle): void {
        const logoProps = new CssProperties();
        const titleProps = new CssProperties();

        if (style.borderRadius) {
            logoProps.tryAdd("border-top-left-radius", `${style.borderRadius.topLeftPx}px`);
            logoProps.tryAdd("border-top-right-radius", `${style.borderRadius.topRightPx}px`);
            logoProps.tryAdd("border-bottom-left-radius", `${style.borderRadius.bottomLeftPx}px`);
            logoProps.tryAdd("border-bottom-right-radius", `${style.borderRadius.bottomRightPx}px`);
        }

        if (style.position) {
            logoProps.tryAdd("object-position", `${style.position.offsetX}% ${style.position.offsetY}%`);
        }

        if (style.thickness) {
            logoProps.tryAdd("border-style", "solid");
            logoProps.tryAdd("border-width", style.thickness);

            titleProps.tryAdd("border-style", "solid");
            titleProps.tryAdd("border-width", style.thickness);
        }

        if (style.borderColor) {
            logoProps.tryAdd("border-color", `${style.borderColor}`);

            titleProps.tryAdd("border-color", `${style.borderColor}`);
        }

        this.styleService.setStyle(element, `#${element.id} .logo`, logoProps.data);
        this.styleService.setStyle(element, `#${element.id} .title`, titleProps.data);
    }

    private renderContentStyles(element: HTMLElement, style: ILogoStyle): void {
        const contentProps = new CssProperties();

        contentProps.tryAdd("justify-content", style.logoAlignment);

        this.styleService.setStyle(element, `#${element.id} .widget-content`, contentProps.data);
    }

    private logoOrTitleNeedsToBeRerendered(element: HTMLElement, data: ILogoWidgetData): boolean {
        const oldLogoId = element.getAttribute("data-logo-id");

        const logoImage = element.querySelector<HTMLImageElement>(".logo");

        const shouldRerenderLogo = oldLogoId == null
            || data.preferredLogoId == null
            || logoImage == null
            || oldLogoId !== data.preferredLogoId;

        return shouldRerenderLogo;
    }

    private linkNeedsToBeRerendered(element: HTMLElement, data: ILinkData | undefined): boolean {
        const oldLink = element.querySelector<HTMLAnchorElement>("a");

        if (!data && !oldLink) {
            return false;
        }

        if (!data || !oldLink) {
            return true;
        }

        const oldLinkValue = oldLink.getAttribute("href");

        switch (data.type) {
            case "page":
                return oldLink.target !== (data as IElementWithTarget)?.target
                    || oldLinkValue !== `/${data.value}`;

            case "url":
                return oldLink.target !== (data as IElementWithTarget)?.target
                    || oldLinkValue !== data.value;

            default:
                return oldLinkValue !== data.value;
        }
    }

    private rerenderLink(element: HTMLElement, data: ILinkData | undefined): void {
        data ? this.renderLink(element, data) : this.removeLink(element);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected cleanupInternal(element: HTMLElement): void {
        // nothing
    }

    private cleanupStyle(element: HTMLElement): void {
        const selectors = this.getContentSelectorsInternal(element);
        if (selectors && selectors.length > 0) {
            selectors.forEach(selector => this.styleService.clearStyle(element, selector));
        }
    }

    protected getContentSelectorsInternal(element: HTMLElement): Array<string> {
        return [`#${element.id}`, `#${element.id} .widget-content`, `#${element.id} .logo`, `#${element.id} .title`];
    }

}
